import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import '../Blog.css';

const markdownList = require('../markdownList.json');

const sortMarkdown = (list) =>
  [...list]
    .sort((a, b) => {
      const getNums = (title) => {
        const s = title.match(/S(\d+)/i);
        const p = title.match(/(\d+)(?:\.(\d+))?\.?\s*Kısım/i);
        return {
          season: s ? parseInt(s[1], 10) : 1,
          part: p ? parseInt(p[1], 10) : 0,
          sub: p && p[2] ? parseInt(p[2], 10) : 0,
        };
      };
      const A = getNums(a.title);
      const B = getNums(b.title);
      return A.season - B.season || A.part - B.part || A.sub - B.sub;
    })
    .reverse();

const chapterList = sortMarkdown(markdownList).filter(({ slug }) => !slug.includes('taslak'));

// Android durum/navigasyon çubuğu rengi theme-color meta etiketiyle belirlenir
const themeColors = {
  'theme-old': '#06222f',
  'theme-dark': '#121218',
  'theme-sepia': '#efe2c4',
  'theme-light': '#ffffff',
};

class Blog extends React.Component {
  state = {
    content: '# Periler hikayeyi getiriyor...',
    isPrevDisabled: false,
    isNextDisabled: false,
    totalViews: 0,
    rating: 0,
    averageRating: null,
    ratingCount: null,
    isRatingDisabled: false,
    userRating: null,
    themes: ['theme-sepia', 'theme-dark', 'theme-old'],
    theme: localStorage.getItem('theme') || 'theme-old',
    comments: [],
    userName: '',
    newComment: '',
    isNameRequired: false,
    errorMessage: '',
  };

  componentDidMount() {
    const { slug } = this.props.params;
    this.loadMarkdown(slug);
    this.checkNavigation(slug);
    this.handleViews();
    this.fetchAverageRating(slug);
    this.fetchComments(slug);
    document.body.className = this.state.theme;
    this.applySystemBarColor(this.state.theme);
  }

  componentDidUpdate(prevProps, prevState) {
    const { slug } = this.props.params;
    if (slug !== prevProps.params.slug) {
      this.loadMarkdown(slug);
      this.checkNavigation(slug);

      if (slug.includes('taslak')) {
        this.setState({ isRatingDisabled: true, userRating: null, averageRating: null, ratingCount: null });
      } else {
        this.setState({ isRatingDisabled: false });
        this.handleViews();
        this.fetchAverageRating(slug);
        this.fetchComments(slug);
      }
    }

    if (prevState.theme !== this.state.theme) {
      document.body.className = this.state.theme;
      this.applySystemBarColor(this.state.theme);
      localStorage.setItem('theme', this.state.theme);
    }
  }

  applySystemBarColor = (theme) => {
    let meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', themeColors[theme] || '#07070b');
  };

  removeZero = (slug) => {
    return slug.replace(/-(0\d+)$/, (match, p1) => `-${p1.replace(/^0+/, '')}`);
  };

  loadMarkdown = (slug) => {
    const isZero = slug.split('-').pop().startsWith('0');

    import(`../markdown/${this.removeZero(slug)}.md`)
      .then((module) => fetch(module.default))
      .then((res) => res.text())
      .then((text) => {
        if (isZero) {
          text = text
            .replace(/'la /g, 'ile')
            .replace(/Mecnun/g, 'Mertcan')
            .replace(/Furkan/g, 'Fatih')
            .replace(/Emre/g, 'Efe')
            .replace(/Zeyo/g, 'Zehra')
            .replace(/Senyao/g, 'Seda')
            .replace(/Ensar/g, 'Ertuğ')
            .replace(/Turkish Gamer/g, 'Tuğba')
            .replace(/Turkish/g, 'Tuğba');

          const titleLineIndex = text.indexOf('\n');
          if (titleLineIndex !== -1) {
            let titleLine = text.substring(0, titleLineIndex).trim();
            titleLine += ' - <a>Global Edition</a>';
            text = titleLine + text.substring(titleLineIndex);
          }
        }

        this.setState({ content: text });
      }).catch((err) => console.error('Markdown yüklenemedi:', err));
  };

  getAdjacentSlug = (slug, step) => {
    const isZeroPadded = slug.split('-').pop().startsWith('0');
    const canonical = this.removeZero(slug);
    const index = chapterList.findIndex((item) => item.slug === canonical);
    if (index === -1) return null;

    const target = chapterList[index + step];
    if (!target) return null;

    if (isZeroPadded) {
      const num = target.slug.split('-').pop();
      let padded = num.padStart(3, '0');
      if (padded.startsWith('00')) padded = padded.slice(1);
      return target.slug.replace(/(\d+)$/, padded);
    }
    return target.slug;
  };

  checkNavigation = async (slug) => {
    const prevSlug = this.getAdjacentSlug(slug, 1);
    const nextSlug = this.getAdjacentSlug(slug, -1);
    const checkExistence = async (s) => (s ? import(`../markdown/${this.removeZero(s)}.md`).then(() => true).catch(() => false) : false);

    this.setState({
      isPrevDisabled: !(await checkExistence(prevSlug)),
      isNextDisabled: !(await checkExistence(nextSlug)),
    });
  };

  handleNavigation = (slug) => {
    this.loadMarkdown(slug);
    this.fetchComments(slug);
    this.checkNavigation(slug);
    this.props.history.push(`/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  handleViews = async () => {
    try {
      const response = await fetch(`https://darknight-api.vercel.app/view?slug=${this.removeZero(this.props.params.slug)}`);
      const data = await response.json();
      this.setState({ totalViews: data.totalViews });
    } catch (error) {
      console.error('Görüntülenme sayısı alınırken hata oluştu:', error);
    }
  };

  submitRating = async (rating) => {
    try {
      const response = await fetch(`https://darknight-api.vercel.app/rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, slug: this.removeZero(this.props.params.slug) }),
      });

      if (response.ok) {
        this.setState({ isRatingDisabled: false, userRating: rating });
        this.fetchAverageRating(this.props.params.slug);
      }
    } catch (error) {
      console.error('Puan gönderme hatası:', error);
    }
  };

  fetchAverageRating = async (slug) => {
    try {
      const response = await fetch(`https://darknight-api.vercel.app/rating?slug=${this.removeZero(slug)}`);
      const data = await response.json();
      this.setState({
        averageRating: data.averageRating,
        ratingCount: data.ratingCount,
        isRatingDisabled: this.state.content.length < 999 ? true : false,
        userRating: data.userHasRated ? data.userRating : null,
        rating: data.userHasRated ? data.userRating : 0,
      });
    } catch (error) {
      console.error('Ortalama puan alma hatası:', error);
    }
  };

  handleRating = (rating) => {
    if (!this.state.isRatingDisabled) {
      this.setState({ rating });
      this.submitRating(rating);
    }
  };

  fetchComments = async (slug) => {
    try {
      const response = await fetch(`https://darknight-api.vercel.app/comments?slug=${slug}`);
      const { comments } = await response.json();
      this.setState({ comments });
    } catch (error) {
      console.error('Yorumlar alınırken hata oluştu:', error);
    }
  };

  handleAddComment = async () => {
    const { slug } = this.props.params;
    const { userName, newComment } = this.state;

    if (!userName || !newComment) return this.setState({ errorMessage: 'Tüm alanların doldurulması zorunludur.' });

    try {
      const response = await fetch(`https://darknight-api.vercel.app/comment?slug=${slug}&user=${encodeURIComponent(userName)}&comment=${encodeURIComponent(newComment)}`, { method: 'POST' });

      if (response.ok) {
        this.setState({ newComment: '' });
        alert('Yorumun başarıyla eklendi!');
        this.fetchComments(slug);
      } else {
        this.setState({ errorMessage: 'Yorum eklerken bir hata oluştu!' });
      }
    } catch (error) {
      console.error('Yorum eklerken hata oluştu:', error);
      this.setState({ errorMessage: 'Yorum eklerken bir hata oluştu!' });
    }
  };

  handleNameChange = (e) => this.setState({ userName: e.target.value });
  handleNewCommentChange = (e) => this.setState({ newComment: e.target.value });

  changeTheme = () => {
    const currentIndex = this.state.themes.indexOf(this.state.theme);
    const nextIndex = (currentIndex + 1) % this.state.themes.length;
    this.setState({ theme: this.state.themes[nextIndex] });
    localStorage.setItem('theme', this.state.themes[nextIndex]);
  };

  render() {
    const { slug } = this.props.params;

    const formatDate = (isoDate) => new Date(isoDate).toLocaleDateString('tr-TR').replaceAll('.', '-');
    const generateColorFromNumber = (n) => `#${(Math.abs(n) % 16777215).toString(16).padStart(6, '0')}`;

    return (
      <div className={`reader ${this.state.theme}`}>
        <div className="reader-nav">
          <a href="/" className="nav-btn" title="Ana Sayfa" aria-label="Ana Sayfa">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M12 3 2 12h3v9h5v-6h4v6h5v-9h3z" />
            </svg>
          </a>

          <div className="nav-arrows">
            <button type="button" className="nav-btn nav-arrow" onClick={() => this.handleNavigation(this.getAdjacentSlug(slug, 1))} disabled={this.state.isPrevDisabled} title="Önceki Bölüm" aria-label="Önceki Bölüm">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button type="button" className="nav-btn nav-arrow" onClick={() => this.handleNavigation(this.getAdjacentSlug(slug, -1))} disabled={this.state.isNextDisabled} title="Sonraki Bölüm" aria-label="Sonraki Bölüm">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>

          <button type="button" className="nav-btn" onClick={this.changeTheme} title="Temayı değiştir" aria-label="Temayı değiştir">
            <img src="/sun.webp" alt="" draggable={false} />
          </button>
        </div>

        <div className="reader-body">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{this.state.content}</ReactMarkdown>
        </div>

        <div className="reader-nav reader-nav-bottom">
          <button type="button" onClick={() => this.handleNavigation(this.getAdjacentSlug(slug, 1))} disabled={this.state.isPrevDisabled}>
            ← Önceki
          </button>
          <button type="button" onClick={() => this.handleNavigation(this.getAdjacentSlug(slug, -1))} disabled={this.state.isNextDisabled}>
            Sonraki →
          </button>
        </div>

        <div className="author-box">
          <div className="comment author">
            <div className="left">
              <div className="profile-3d">
                <div className="flip">
                  <div className="face front"></div>
                  <div className="face back"></div>
                </div>
              </div>

              <div className="column-div">
                <h2>DarkNight</h2>
                <strong>Yazar</strong>
              </div>
            </div>
          </div>

          {(this.props.params.slug.includes("zekalar2") || this.props.params.slug.includes("zekalar3")) && Number(window.location.pathname.match(/\d+/)?.[0].replace(/^0+/, '')) > 0 ?
            <div className="comment author">
              <div className="left">
                <img src="/pwillop.jpg" alt={"Editör Fotoğrafı"} />
                <div className="column-div">
                  <h2>Pewelop</h2>
                  <strong>Editör</strong>
                </div>
              </div>
            </div>
            : <></>}
        </div>

        <div className="alert">
          Haberdar olmak için <a href="https://whatsapp.com/channel/0029VatHmfJ6WaKsWVWagm18">WhatsApp kanalı</a> üzerinden takip edin!
        </div>

        <div className="rating-box">
          <div className="rating-heading">Okuyucu puanları {this.state.ratingCount ? `(${this.state.ratingCount})` : ''}</div>

          <div className="user-rating">
            <div className="average-rating">{this.state.averageRating?.slice(0, -1)}</div>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((i) => (
                <span
                  key={i}
                  className={i <= this.state.rating ? 'star selected' : 'star'}
                  onClick={() => this.handleRating(i)}
                  style={{ cursor: this.state.isRatingDisabled ? 'not-allowed' : 'pointer', color: i <= this.state.rating ? 'gold' : 'gray' }}
                >
                  ★
                </span>
              ))}
            </div>
          </div>

          <div className="rating-note">
            {this.state.userRating ? `Verdiğiniz Puan: ${this.state.userRating}` : 'Henüz puan vermediniz.'}
          </div>
        </div>

        <div className="rating-box comments-box">
          {!this.state.isRatingDisabled && <div className="rating-heading">Yorum Yap!</div>}

          {!this.state.isRatingDisabled && (
            <div className="comment-form">
              <input
                type="text"
                placeholder="Adınızı girin..."
                value={this.state.userName}
                onChange={this.handleNameChange}
              />
              <textarea
                placeholder="Yorumunuzu yazın..."
                value={this.state.newComment}
                onChange={this.handleNewCommentChange}
              ></textarea>
              <button onClick={this.handleAddComment}>Yorum Yap</button>

              {this.state.errorMessage && <div className="error-message">{this.state.errorMessage}</div>}
            </div>
          )}

          <div className="rating-heading">Okuyucu Yorumları</div>

          <div className="comments-list">
            {this.state.comments.length > 0 ? this.state.comments.map((comment, index) => (
              <div key={index} className="comment">
                <div className="left">
                  <img
                    src={`https://ui-avatars.com/api/?name=${comment.user_name}&background=${generateColorFromNumber(comment.user_name.match(/\d+$/) ? comment.user_name.match(/\d+$/)[0] : 0).slice(1)}&color=fff`}
                    alt={comment.user_name}
                  />
                  <strong>{comment.user_name}</strong>
                </div>

                <div className="right">
                  <p>{comment.comment_text}</p>
                  <span className="date">{formatDate(comment.created_at)}</span>
                </div>
              </div>
            )) : <p className="no-comments">Henüz yorum yapılmamış.</p>}
          </div>
        </div>
      </div>
    );
  }
}

export default (props) => <Blog {...props} params={useParams()} history={useHistory()} location={useLocation()} />;
